import {
    FileParameter,
    IIssueActivityAlertsUpdateResponse,
    IIssueActivitySavingsUpdateResponse,
    IIssueChartDataChild,
    IIssueResponseBase,
    IssueActivityChartDataResponse,
    IssueAlertsUpdateRequest,
    IssueArchiveRequest,
    IssueChartDataFaultStatus,
    IssueChartDataUpdateRequest,
    IssueDescriptionUpdateRequest,
    IssueFaultUpdateRequest,
    IssueFlagUpdateRequest,
    IssueHardwareHealthResponse,
    IssueHealthRangeCreateRequest,
    IssueHealthRangeUpdateRequest,
    IssueInternalUpdateRequest,
    IssueMachineHealthResponse,
    IssuePriorityUpdateRequest,
    IssueRecommendedActionUpdateRequest,
    IssueRemoveRequest,
    IssueResolvedDateUpdateRequest,
    IssueSavingsUpdateApplicableRequest,
    IssueSavingsUpdateRequest,
    IssueSeverityUpdateRequest,
    IssueStatusType,
    IssueStatusUpdateRequest,
    SavingsApprovalUpdateRequest,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { IssueChartDataInfo } from 'pages/Desk/issues/chart_data/types';
import {
    IssueEvent,
    useTriggerIssueEvent,
} from 'pages/Desk/issues/issue_details/IssueEventsProvider';
import {
    useAutoAdvanceQueue,
    useDeskQueueState,
} from 'pages/Desk/issues/issue_details/useDraftIssueQueue';
import {
    getChartDataCreateRequest,
    getIssueChartDataAnnotation,
} from 'pages/Desk/issues/issue_modals/shared';
import { useIssueStatuses } from 'pages/Desk/issues/useIssueStatuses';
import { ReactNode, useMemo } from 'react';
import { useHistory } from 'react-router';
import { ARCHIVED_ISSUE_STATUSES } from 'shared/issue_filters_context/types';
import { AnnotationConfig } from 'shared/svg_annotations/types';
import { getIssueDetailsRoute, getIssuesPageRoute } from 'utils/RouteHelper';

export type CommentFile = Readonly<{
    id: string;
    file: File;
}>;

export type CreateCommentOptions = Readonly<{
    replyToCommentId?: string;
    comment?: string;
    files?: CommentFile[];
}>;

export type EditCommentOptions = Readonly<{
    activityId: string;
    comment?: string;
    filesToAdd?: CommentFile[];
    fileIdsToRemove?: string[];
}>;

export type UpdateChartDataOptions = Readonly<{
    chartDataId: string;
    title: string;
    annotations: AnnotationConfig[];
    faultStatus: IssueChartDataFaultStatus;
}>;

export type UpdateSavingsOptions = Readonly<{
    savingsRequest: IssueSavingsUpdateRequest;
    verifyRequest?: SavingsApprovalUpdateRequest;
}>;

export type ApproveSavingsOptions = Readonly<{
    savingsId: string;
    request: SavingsApprovalUpdateRequest;
}>;

export type UpdateHealthRangeOptions = Readonly<{
    healthRangeId: string;
    request: IssueHealthRangeUpdateRequest;
}>;

export type UpdateFlagOptions = Readonly<{
    newValue: boolean;
    shouldReceiveNotifications: boolean;
}>;

type OnSuccessOptions = Readonly<{
    content?: ReactNode;
    timeoutMs?: number;
    event?: IssueEvent;
}>;

export function useUpdateIssue(issue: IIssueResponseBase) {
    const { push } = useHistory();
    const { data } = useIssueStatuses();
    const { showToast } = useNotificationContext();
    const triggerIssueEvent = useTriggerIssueEvent();

    const { queuedIssueIds, setQueuedIssueIds } = useDeskQueueState();

    const autoAdvanceQueue = useAutoAdvanceQueue(
        issue,
        queuedIssueIds,
        setQueuedIssueIds,
        push
    );

    const {
        issue: { canIndicateFaultOnChartData },
    } = usePermissions();

    const newIssueStatus = useMemo(
        () => data?.Result.find(({ StatusType }) => StatusType === IssueStatusType.New),
        [data?.Result]
    );

    const onError = (content: string) => {
        showToast({ type: 'error', content });
    };

    const onSuccess = ({ content, event, timeoutMs }: OnSuccessOptions) => {
        if (content) {
            showToast({ type: 'success', content, timeoutMs });
        }
        if (event) {
            triggerIssueEvent(event);
        }
    };

    const { mutate: updateStatus, isPending: isUpdatingStatus } = useMutation({
        mutationFn: (statusId: string) =>
            sdClient.updateIssueStatus({
                id: issue.Id,
                body: new IssueStatusUpdateRequest({ StatusId: statusId }),
            }),

        onError: () => onError('Failed to update issue status'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                },
            }),
    });

    const { mutate: updateSeverity, isPending: isUpdatingSeverity } = useMutation({
        mutationFn: (severity: number) =>
            sdClient.updateIssueSeverity({
                id: issue.Id,
                body: new IssueSeverityUpdateRequest({ Severity: severity }),
            }),

        onError: () => onError('Failed to update issue severity'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                },
            }),
    });

    const { mutate: updatePriority, isPending: isUpdatingPriority } = useMutation({
        mutationFn: (priority: number) =>
            sdClient.updateIssuePriority({
                id: issue.Id,
                body: new IssuePriorityUpdateRequest({ Priority: priority }),
            }),

        onError: () => onError('Failed to update issue priority'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                },
            }),
    });

    const { mutate: updateFaultTypes, isPending: isUpdatingFaultTypes } = useMutation({
        mutationFn: (faultTypeIds: string[]) =>
            sdClient.updateFaults({
                id: issue.Id,
                body: new IssueFaultUpdateRequest({ FaultTypeIds: faultTypeIds }),
            }),

        onError: () => onError('Failed to update issue fault types'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                },
            }),
    });

    const { mutate: updateAlerts, isPending: isUpdatingAlerts } = useMutation({
        mutationFn: (alertIds: string[]) =>
            sdClient.updateIssueAlerts({
                id: issue.Id,
                body: new IssueAlertsUpdateRequest({ AlertIds: alertIds }),
            }),

        onError: () => onError('Failed to update issue alerts'),

        // TODO: the explicit typing is because the API can return 204 in cases that
        // don't produce a updated alerts activity (ie an update that doesn't change the selected alerts)
        // This can be removed once the SDK understands that the request will possibly return undefined
        onSuccess: (activity: IIssueActivityAlertsUpdateResponse | null) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity?.Id ?? issue.Id,
                },
            }),
    });

    const { mutate: updateSavings, isPending: isUpdatingSavings } = useMutation({
        mutationFn: async ({ savingsRequest, verifyRequest }: UpdateSavingsOptions) => {
            const savingsActivity = await sdClient.updateIssueSavings({
                id: issue.Id,
                body: savingsRequest,
            });

            if (verifyRequest) {
                await sdClient.approveSavingsForIssue({
                    issueId: issue.Id,
                    body: verifyRequest,
                });
            }

            return savingsActivity;
        },

        onError: () => onError('Failed to update issue savings'),

        // TODO: the explicit typing is because the API can return 204 in cases that
        // don't produce a savings activity (ie updating just the savings value tags)
        // This can be removed once the SDK understands that the request will possibly return undefined
        onSuccess: (activity: IIssueActivitySavingsUpdateResponse | null) =>
            onSuccess({
                content: 'Successfully updated issue savings',
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity?.Id ?? issue.Id,
                },
            }),
    });

    const {
        mutate: updateSavingsApplicability,
        isPending: isUpdatingSavingsApplicability,
    } = useMutation({
        mutationFn: (request: IssueSavingsUpdateApplicableRequest) =>
            sdClient.updateIssueSavingsApplicability({ id: issue.Id, body: request }),

        onError: () => onError('Failed to update issue savings'),

        onSuccess: (activity) =>
            onSuccess({
                content: 'Successfully updated issue savings',
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                },
            }),
    });

    const { mutate: verifySavings, isPending: isVerifyingSavings } = useMutation({
        mutationFn: ({ savingsId, request }: ApproveSavingsOptions) =>
            sdClient.approveSavings({ id: savingsId, body: request }),

        onError: () => onError('Failed to verify issue savings'),

        onSuccess: (activity) =>
            onSuccess({
                content: 'Successfully verified issue savings',
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                },
            }),
    });

    const { mutate: updateDescription, isPending: isUpdatingDescription } = useMutation({
        mutationFn: async (request: IssueDescriptionUpdateRequest) =>
            sdClient.updateIssueDescription({ id: issue.Id, body: request }),

        onError: () => onError('Failed to update issue description'),

        onSuccess: (activity) =>
            onSuccess({
                content: 'Successfully updated issue description',
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                },
            }),
    });

    const {
        mutate: updateRecommendation,
        isPending: isUpdatingRecommendation,
    } = useMutation({
        mutationFn: async (request: IssueRecommendedActionUpdateRequest) =>
            sdClient.updateIssueRecommendedAction({ id: issue.Id, body: request }),

        onError: () => onError('Failed to update issue recommendation'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                },
            }),
    });

    const { mutate: updateResolvedDate, isPending: isUpdatingResolvedDate } = useMutation(
        {
            mutationFn: async (resolvedDate: Date | undefined) =>
                await sdClient.updateIssueResolvedDate({
                    id: issue.Id,
                    body: new IssueResolvedDateUpdateRequest({
                        DateResolved: resolvedDate,
                    }),
                }),

            onError: () => onError('Failed to update issue resolved date'),

            onSuccess: (activity) =>
                onSuccess({
                    event: {
                        type: 'activity',
                        action: 'create',
                        activityId: activity.Id,
                    },
                }),
        }
    );

    const { mutate: saveNewChartData, isPending: isSavingNewChartData } = useMutation({
        mutationFn: async (chartData: IssueChartDataInfo[]) =>
            issue.IssueStatus.StatusType === IssueStatusType.Draft
                ? sdClient.addInitialChartData({
                      id: issue.Id,
                      body: chartData.map((info) =>
                          getChartDataCreateRequest(info, canIndicateFaultOnChartData)
                      ),
                  })
                : sdClient.createIssueChartData({
                      id: issue.Id,
                      body: chartData.map((info) =>
                          getChartDataCreateRequest(info, canIndicateFaultOnChartData)
                      ),
                  }),

        onError: () => onError('Failed to added issue plot'),

        onSuccess: (result) =>
            onSuccess({
                event:
                    result instanceof IssueActivityChartDataResponse
                        ? {
                              type: 'activity',
                              action: 'create',
                              activityId: result.Id,
                          }
                        : { type: 'issue', action: 'update', issueId: issue.Id },
            }),
    });

    const { mutate: updateChartData, isPending: isUpdatingChartData } = useMutation({
        mutationFn: async (options: UpdateChartDataOptions) => {
            if (issue instanceof IssueHardwareHealthResponse) {
                throw new Error(
                    `Attempted to update chart data for a hardware issue, issue id: ${issue.Id}`
                );
            }

            const request = new IssueChartDataUpdateRequest({
                Title: options.title,
                ChartAnnotations: options.annotations.map(getIssueChartDataAnnotation),
                FaultStatus: canIndicateFaultOnChartData
                    ? options.faultStatus
                    : undefined,
            });

            const isInitialChartData =
                issue instanceof IssueMachineHealthResponse &&
                issue.ChartDataIds.includes(options.chartDataId);

            return isInitialChartData
                ? sdClient.updateInitialChartData({
                      id: issue.Id,
                      chartDataId: options.chartDataId,
                      body: request,
                  })
                : sdClient.updateIssueChartData({
                      id: issue.Id,
                      chartDataId: options.chartDataId,
                      body: request,
                  });
        },

        onError: () => onError('Failed to updated issue plot'),

        onSuccess: (result) =>
            onSuccess({
                content: 'Successfully updated issue plot',
                event:
                    result instanceof IssueActivityChartDataResponse
                        ? {
                              type: 'activity',
                              action: 'update',
                              activityId: result.Id,
                          }
                        : { type: 'issue', action: 'update', issueId: issue.Id },
            }),
    });

    const { mutate: deleteChartData, isPending: isDeletingChartData } = useMutation({
        mutationFn: (chartData: IIssueChartDataChild) =>
            sdClient.deleteIssueChartData({ id: issue.Id, chartDataId: chartData.Id }),

        onSuccess: () => {
            onSuccess({
                content: 'Successfully deleted issue plot',
            });
            // TODO: find a better way to handle when chart data id deleted
            window.location.reload();
        },

        onError: () => onError('Failed to delete issue plot'),
    });

    const { mutate: publishDraftIssue, isPending: isPublishingDraftIssue } = useMutation({
        mutationFn: (autoCreateMaintenanceTask?: boolean) => {
            if (!newIssueStatus) {
                throw new Error('Missing IssueStatusType.New');
            }
            return sdClient.updateIssueStatus({
                id: issue.Id,
                body: new IssueStatusUpdateRequest({
                    StatusId: newIssueStatus.Id,
                    AutoCreateMaintenanceTaskForPublish: autoCreateMaintenanceTask,
                }),
            });
        },

        onError: () => onError('Failed to publish Draft issue'),

        onSuccess: (activity) => {
            onSuccess({
                content: 'Your Draft issue has been published as a New issue',
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                },
            });

            if (queuedIssueIds.length === 0 || !queuedIssueIds.includes(issue.Id)) {
                push(getIssueDetailsRoute(issue.Location.LocationId, issue.Id));
            } else {
                autoAdvanceQueue();
            }
        },
    });

    const { mutate: removeDraftIssue, isPending: isRemovingDraftIssue } = useMutation({
        mutationFn: (request: IssueRemoveRequest) =>
            sdClient.removeIssue({ id: issue.Id, body: request }),

        onError: () => onError('Failed to delete Draft issue'),

        onSuccess: () => {
            onSuccess({
                content: 'Your Draft issue has been deleted',
            });

            if (queuedIssueIds.length === 0 || !queuedIssueIds.includes(issue.Id)) {
                const issueMode =
                    issue instanceof IssueMachineHealthResponse
                        ? 'machineHealth'
                        : 'hardwareHealth';
                push(getIssuesPageRoute(issue.Location.LocationId, issueMode, 'draft'));
            } else {
                autoAdvanceQueue();
            }
        },
    });

    const { mutate: createComment, isPending: isCreatingComment } = useMutation({
        mutationFn: async (options: CreateCommentOptions) =>
            sdClient.createIssueComment({
                id: issue.Id,
                comment: options.comment,
                replyToCommentId: options.replyToCommentId,
                files: options.files?.map<FileParameter>((commentFile) => ({
                    fileName: commentFile.file.name,
                    data: commentFile.file,
                })),
            }),

        onError: () => onError('Failed to add comment'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                },
            }),
    });

    const { mutate: updateComment, isPending: isUpdatingComment } = useMutation({
        mutationFn: async (options: EditCommentOptions) =>
            sdClient.updateIssueComment({
                id: issue.Id,
                activityId: options.activityId,
                comment: options.comment,
                filesToAdd: options.filesToAdd?.map((commentFile) => ({
                    data: commentFile.file,
                    fileName: commentFile.file.name,
                })),
                fileIdsToDelete: options.fileIdsToRemove,
            }),

        onError: () => onError('Failed to update comment'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'update',
                    activityId: activity.Id,
                },
            }),
    });

    const { mutate: deleteComment, isPending: isDeletingComment } = useMutation({
        mutationFn: async (commentId: string) =>
            sdClient.deleteIssueComment({ id: issue.Id, activityId: commentId }),

        onError: () => onError('Failed to delete comment'),

        onSuccess: (activity) =>
            onSuccess({
                event: {
                    type: 'activity',
                    action: 'create',
                    activityId: activity.Id,
                },
            }),
    });

    const { mutate: archiveIssue, isPending: isArchivingIssue } = useMutation({
        mutationFn: async (request: IssueArchiveRequest) =>
            sdClient.archiveIssue({ id: issue.Id, body: request }),

        onError: () => onError('Failed to update issue status'),

        onSuccess: () =>
            onSuccess({
                event: { type: 'issue', action: 'update', issueId: issue.Id },
            }),
    });

    const {
        mutate: updateFaultLocation,
        isPending: isUpdatingFaultLocation,
    } = useMutation({
        mutationFn: async (group: string) => {
            await sdClient.updateIssueGroup({ id: issue.Id, body: group });
        },

        onSuccess: () => {
            onSuccess({
                content: 'Successfully updated fault location',
                event: { type: 'issue', action: 'update', issueId: issue.Id },
            });
        },

        onError: () => onError('Failed to update fault location'),
    });

    const { mutate: createHealthRange, isPending: isCreatingHealthRange } = useMutation({
        mutationFn: (request: IssueHealthRangeCreateRequest) =>
            sdClient.createIssueHealthRange({
                id: issue.Id,
                body: request,
            }),

        onSuccess: () =>
            onSuccess({
                content: 'Successfully created issue health range',
            }),

        onError: () => onError('Failed to create issue health range'),
    });

    const { mutate: updateHealthRange, isPending: isUpdatingHealthRange } = useMutation({
        mutationFn: ({ healthRangeId, request }: UpdateHealthRangeOptions) =>
            sdClient.updateIssueHealthRange({ id: healthRangeId, body: request }),

        onSuccess: () =>
            onSuccess({
                content: 'Successfully updated issue health range',
            }),

        onError: () => onError('Failed to update issue health range'),
    });

    const { mutate: deleteHealthRange, isPending: isDeletingHealthRange } = useMutation({
        mutationFn: (healthRangeId: string) =>
            sdClient.deleteIssueHealthRange({ id: healthRangeId }),
        onSuccess: () =>
            onSuccess({
                content: 'Successfully deleted health range',
            }),

        onError: () => onError('Failed to delete health range'),
    });

    const {
        mutate: updateHardwareHealthIssueIsInternal,
        isPending: isUpdatingHardwareHealthIssueIsInternal,
    } = useMutation({
        mutationFn: (newValue: boolean) =>
            sdClient.updateHardwareHealthInternal({
                id: issue.Id,
                body: new IssueInternalUpdateRequest({ IsInternal: newValue }),
            }),

        onSuccess: () =>
            onSuccess({
                content: 'Successfully updated KCF Only status',
                event: { type: 'issue', action: 'update', issueId: issue.Id },
            }),

        onError: () => onError('Failed to update KCF Only status'),
    });

    const {
        mutate: updateIssueIsFlagged,
        isPending: isUpdatingIssueIsFlagged,
    } = useMutation({
        mutationFn: async ({
            newValue,
            shouldReceiveNotifications,
        }: UpdateFlagOptions) => {
            const isArchived = ARCHIVED_ISSUE_STATUSES.includes(
                issue.IssueStatus.StatusType
            );

            if (newValue) {
                await sdClient.flagIssue({
                    id: issue.Id,
                    body: new IssueFlagUpdateRequest({
                        ShouldReceiveNotifications: shouldReceiveNotifications,
                    }),
                });
                return { flagged: true, shouldReceiveNotifications, isArchived };
            }
            await sdClient.unflagIssue({ id: issue.Id });
            return { flagged: false, shouldReceiveNotifications: false, isArchived };
        },

        onSuccess: (result) =>
            onSuccess({
                event: {
                    type: 'flag',
                    action: 'update',
                    issueId: issue.Id,
                    flagInfo: result,
                },
            }),
        onError: () => onError('Failed to update issue flagged status'),
    });

    return {
        updateRecommendation,
        updateDescription,
        updateStatus,
        updateSeverity,
        updatePriority,
        updateFaultTypes,
        updateAlerts,
        updateSavings,
        updateSavingsApplicability,
        verifySavings,
        saveNewChartData,
        publishDraftIssue,
        removeDraftIssue,
        createComment,
        updateComment,
        deleteComment,
        updateChartData,
        deleteChartData,
        archiveIssue,
        updateFaultLocation,
        createHealthRange,
        updateHealthRange,
        deleteHealthRange,
        updateHardwareHealthIssueIsInternal,
        updateIssueIsFlagged,
        updateResolvedDate,
        isUpdatingRecommendation,
        isUpdatingDescription,
        isUpdatingStatus,
        isUpdatingSeverity,
        isUpdatingPriority,
        isUpdatingFaultTypes,
        isUpdatingAlerts,
        isUpdatingSavings,
        isUpdatingSavingsApplicability,
        isVerifyingSavings,
        isSavingNewChartData,
        isPublishingDraftIssue,
        isRemovingDraftIssue,
        isCreatingComment,
        isUpdatingComment,
        isDeletingComment,
        isUpdatingChartData,
        isDeletingChartData,
        isArchivingIssue,
        isUpdatingFaultLocation,
        isCreatingHealthRange,
        isUpdatingHealthRange,
        isDeletingHealthRange,
        isUpdatingHardwareHealthIssueIsInternal,
        isUpdatingIssueIsFlagged,
        isUpdatingResolvedDate,
    };
}
